import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Filter state that a link can set.
 *
 * ⚠️ A DASHBOARD TILE THAT DOES NOT DRILL IS A DEAD END. Every figure on that
 * screen is a count of records, and the one question anybody has on reading it
 * is "which ones?" — a tile that states 3 open shifts and cannot show them makes
 * the reader go and find them by hand, which is the work the tile was supposed
 * to have done.
 *
 * So each screen takes its filters from the query string. The tile links carry
 * them, the screen opens already filtered, and the URL stays truthful as the
 * filter changes — which is what makes the view shareable, bookmarkable, and
 * survivable across a browser back button.
 *
 * `replace: true` on the write: changing a filter is not a new place, and a
 * history stack full of filter permutations means the back button no longer
 * leaves the screen.
 *
 * Usage:
 *   const [f, setF] = useUrlFilters({ status: "all", from: "", to: "" });
 *   <Select value={f.status} onValueChange={(v) => setF({ status: v })} />
 */
export function useUrlFilters(defaults) {
  const [params, setParams] = useSearchParams();

  // Read ONCE into state. Reading the URL on every render would fight any
  // control that also holds a draft value, and a date input mid-typing is
  // exactly that.
  const [value, setValue] = useState(() => {
    const out = { ...defaults };
    for (const k of Object.keys(defaults)) {
      const v = params.get(k);
      if (v !== null && v !== "") out[k] = v;
    }
    return out;
  });

  const set = useCallback((patch) => {
    setValue((prev) => {
      const next = { ...prev, ...patch };
      const sp = new URLSearchParams(params);
      for (const [k, v] of Object.entries(next)) {
        // A filter sitting at its default is not worth carrying in the URL —
        // it makes a shared link look specific when it is not.
        if (v === undefined || v === null || v === "" || v === defaults[k]) sp.delete(k);
        else sp.set(k, String(v));
      }
      setParams(sp, { replace: true });
      return next;
    });
  }, [params, setParams, defaults]);

  return [value, set];
}

/** Build a dashboard drill-through link: `to("/schedule", { status: "open" })`. */
export function to(path, params) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const q = sp.toString();
  return q ? `${path}?${q}` : path;
}
